export const BASE_RULE_SETS = [
  {
    id: 'rs_default',
    name: 'Default',
    enabled: true,
    rules: [
      { id: 'rule_style', type: 'match_all', field: 'customer_style_number', enabled: true, name: 'Same Style #' },
      { id: 'rule_size',  type: 'match_all', field: 'size',                  enabled: true, name: 'Same Size'    },
      { id: 'rule_color', type: 'match_all', field: 'color',                 enabled: true, name: 'Same Color'   },
      { id: 'rule_handover', type: 'max_days', field: 'bshipdate', max: 21, enabled: true, name: 'Bshipdate within 21 days' },
    ]
  }
];

// Keep backward compat for GroupsPage which passes flat rule arrays
export const BASE_RULES = BASE_RULE_SETS[0].rules;

/**
 * Per-vendor default rule sets.
 * Keys match the vendor `value` strings used in the UI.
 * Vendors not listed here start with NO rule sets (the user must create their own).
 */
export const VENDOR_DEFAULT_RULE_SETS = {
  american_eagle: BASE_RULE_SETS,
  // Add other vendors here when their default rules are known.
  // Until then, omitting a vendor means it starts with an empty rule set list.
};

export const norm = (val) => val == null ? '' : String(val).trim().toLowerCase();

/**
 * Detect conflicts between enabled rule sets.
 *
 * A conflict exists between two rule sets when an item could satisfy BOTH,
 * meaning the second rule set would never be reached for that item.
 * This happens when:
 *  (A) They share the same match_all fields AND both have no value_in to distinguish them
 *      → any item matching one will always match the other too (full duplicate)
 *  (B) They have value_in rules on the same field whose permitted value sets overlap
 *      → an item carrying one of the overlapping values matches both rule sets
 *
 * Returns an array of conflict objects:
 *  { rs1Id, rs1Name, rs2Id, rs2Name, fieldConflicts: [{ field, overlappingValues, isDuplicate }] }
 */
export const detectRuleSetConflicts = (ruleSets) => {
  if (!ruleSets || ruleSets.length < 2) return [];
  const conflicts = [];
  const enabled = ruleSets.filter(rs => rs.enabled);

  for (let i = 0; i < enabled.length; i++) {
    for (let j = i + 1; j < enabled.length; j++) {
      const rs1 = enabled[i];
      const rs2 = enabled[j];

      const rs1Rules = (rs1.rules || []).filter(r => r.enabled !== false);
      const rs2Rules = (rs2.rules || []).filter(r => r.enabled !== false);

      const rs1MatchAll = new Set(rs1Rules.filter(r => r.type === 'match_all').map(r => r.field));
      const rs2MatchAll = new Set(rs2Rules.filter(r => r.type === 'match_all').map(r => r.field));
      const rs1ValueIn  = rs1Rules.filter(r => r.type === 'value_in' && r.field);
      const rs2ValueIn  = rs2Rules.filter(r => r.type === 'value_in' && r.field);

      const fieldConflicts = [];

      // (A) Same match_all fields, no value_in on either → complete duplicate
      const sameMatchAll =
        rs1MatchAll.size === rs2MatchAll.size &&
        [...rs1MatchAll].every(f => rs2MatchAll.has(f));
      if (sameMatchAll && rs1ValueIn.length === 0 && rs2ValueIn.length === 0 && rs1MatchAll.size > 0) {
        fieldConflicts.push({ field: null, overlappingValues: [], isDuplicate: true });
      }

      // (B) value_in rules on the same field with overlapping value sets
      for (const r1 of rs1ValueIn) {
        const r2 = rs2ValueIn.find(r => r.field === r1.field);
        if (!r2) continue;
        const vals1 = new Set((r1.values || []).map(v => norm(v)));
        const overlapping = (r2.values || []).filter(v => vals1.has(norm(v)));
        if (overlapping.length > 0) {
          fieldConflicts.push({ field: r1.field, overlappingValues: overlapping, isDuplicate: false });
        }
      }

      if (fieldConflicts.length > 0) {
        conflicts.push({ rs1Id: rs1.id, rs1Name: rs1.name, rs2Id: rs2.id, rs2Name: rs2.name, fieldConflicts });
      }
    }
  }
  return conflicts;
};

export const validateRules = (items, rules) => {
  if (!items || items.length < 1) return [];
  if (!rules || !rules.length) return [];

  const violations = [];

  for (const rule of rules) {
    if (!rule.enabled) continue;

    if (rule.type === 'match_all') {
      const uniq = new Set(items.map(i => norm(i[rule.field])).filter(v => v !== ''));
      if (uniq.size > 1) {
        violations.push(`Rule broken: Items have different "${rule.name || rule.field}" — must be the same.`);
      }
    }
    else if (rule.type === 'value_in') {
      const allowed = (rule.values || []).map(v => norm(v));
      if (allowed.length > 0) {
        const outside = items.filter(i => {
          const v = norm(i[rule.field]);
          return v !== '' && !allowed.some(a => a === v || v.includes(a) || a.includes(v));
        });
        if (outside.length > 0) {
          violations.push(`Rule broken: "${rule.name || rule.field}" — some items have values not in [${(rule.values || []).join(', ')}].`);
        }
      }
    }
    else if (rule.type === 'pack_bulk_web') {
      const uniqPack = new Set(items.map(i => norm(i.pack_method_type)).filter(v => v !== ''));
      if (uniqPack.size > 1) {
        const hasBulkAndWeb = uniqPack.has('bulk') && (uniqPack.has('prepack') || uniqPack.has('web') || uniqPack.has('online'));
        if (hasBulkAndWeb) {
          const pos = new Set(items.map(i => norm(i.customer_po_number)).filter(v => v !== ''));
          if (pos.size > 1) violations.push(`Rule broken: Bulk and Web items must be on the same PO to be grouped together.`);
        } else {
          violations.push(`Rule broken: Items have different Pack method/type — must be the same (unless Bulk+Web on same PO).`);
        }
      }
    }
    else if (rule.type === 'max_days') {
      const dates = items.map(i => i[rule.field]).filter(Boolean).map(d => new Date(d)).filter(d => !isNaN(d));
      if (dates.length > 1) {
        const mn = Math.min(...dates), mx = Math.max(...dates);
        const diffDays = (mx - mn) / (1000 * 60 * 60 * 24);
        if (diffDays > rule.max) {
          violations.push(`Rule broken: "${rule.name}" — dates span ${Math.round(diffDays)} days (max allowed: ${rule.max}).`);
        }
      }
    }
    else if (rule.type === 'isolate') {
      const vals = items.map(i => norm(i[rule.field]));
      const hasIso = vals.some(s => s.includes(rule.value));
      const noIso  = vals.some(s => s !== '' && !s.includes(rule.value));
      if (hasIso && noIso) {
        violations.push(`Rule broken: "${rule.name}" — items with '${rule.value}' must not be mixed with items without it.`);
      }
    }
  }

  return violations;
};
